import { useCallback, useMemo, useState } from 'react';
import { showError, showInfo } from '../../../shared/ui/appToast';
import { buildCustomerMovementHistoryCsv } from '../../infra/history/customerMovementHistoryCsv'
import { getCustomerMovementHistory, getCustomerMovementHistoryForExport } from '../../infra/customerRepository'
import { getCustomerMovementTypeLabel } from '../../domain/customerMovementLabels'
import type { CustomerAccountRow, CustomerMovementRow, CustomerMovementHistoryFilters } from '../../domain/customerTypes';
import { shareOrDownloadCsv } from '../../../shared/csv-core/infra/shareCsv';
import { useAuthStore } from '../../../auth/store/useAuthStore';
import { useEmployeesStore, withSelfOwner } from '../../../team/store/useEmployeesStore';

import { CUSTOMER_HISTORY_PAGE_SIZE } from '../../domain/customerConstants';

type Params = {
  formatMoney: (n: number) => string;
};

export function useCustomerMovementHistoryFlow({ formatMoney }: Params) {
  const user = useAuthStore((s) => s.user);
  const employeesRaw = useEmployeesStore((s) => s.employees);
  const selfOwner = useEmployeesStore((s) => s.selfOwner);
  const employees = useMemo(() => withSelfOwner(employeesRaw, selfOwner), [employeesRaw, selfOwner]);

  const [customerHistoryVisible, setCustomerHistoryVisible] = useState(false);
  const [customerHistoryCustomer, setCustomerHistoryCustomer] = useState<CustomerAccountRow | null>(null);
  const [customerHistoryRows, setCustomerHistoryRows] = useState<CustomerMovementRow[]>([]);
  const [customerHistoryLoading, setCustomerHistoryLoading] = useState(false);
  const [customerHistoryLoadingMore, setCustomerHistoryLoadingMore] = useState(false);
  const [customerHistoryHasMore, setCustomerHistoryHasMore] = useState(false);
  const [customerHistoryOffset, setCustomerHistoryOffset] = useState(0);
  const [customerHistoryTypeFilter, setCustomerHistoryTypeFilter] = useState('all');
  const [customerHistoryFromDate, setCustomerHistoryFromDate] = useState('');
  const [customerHistoryToDate, setCustomerHistoryToDate] = useState('');

  const buildCustomerHistoryFilters = useCallback((): CustomerMovementHistoryFilters => {
    const f: CustomerMovementHistoryFilters = {};
    const from = customerHistoryFromDate.trim();
    const to = customerHistoryToDate.trim();
    if (from) {
      const fromTs = new Date(`${from}T00:00:00`).getTime();
      if (Number.isFinite(fromTs)) {
        f.fromCreatedAt = new Date(fromTs).toISOString();
      }
    }
    if (to) {
      const toTs = new Date(`${to}T23:59:59.999`).getTime();
      if (Number.isFinite(toTs)) {
        f.toCreatedAt = new Date(toTs).toISOString();
      }
    }
    if (customerHistoryTypeFilter !== 'all') {
      f.type = customerHistoryTypeFilter as 'charge' | 'payment';
    }
    return f;
  }, [customerHistoryFromDate, customerHistoryToDate, customerHistoryTypeFilter]);

  const actorLabelByAuthId = useMemo(() => {
    const map: Record<string, string> = {};
    if (user?.id) {
      map[user.id] = user.email ?? user.id;
    }
    for (const e of employees) {
      if (e.authUserId) {
        map[e.authUserId] = e.name;
      }
    }
    return map;
  }, [user, employees]);

  const actorLabel = useCallback(
    (uid: string | null | undefined) => (uid ? actorLabelByAuthId[uid] ?? `${uid.slice(0, 8)}…` : '—'),
    [actorLabelByAuthId],
  );

  const customerTypeOptions = useMemo(
    () => [
      { value: 'all', label: 'Todos' },
      { value: 'charge', label: getCustomerMovementTypeLabel('charge') },
      { value: 'payment', label: getCustomerMovementTypeLabel('payment') },
    ],
    [],
  );

  const openCustomerHistory = useCallback(async (customer: CustomerAccountRow) => {
    setCustomerHistoryCustomer(customer);
    setCustomerHistoryVisible(true);
    setCustomerHistoryLoading(true);
    setCustomerHistoryOffset(0);
    setCustomerHistoryHasMore(false);
    setCustomerHistoryLoadingMore(false);
    setCustomerHistoryTypeFilter('all');
    setCustomerHistoryFromDate('');
    setCustomerHistoryToDate('');
    void useEmployeesStore.getState().loadEmployees().catch(() => {});
    try {
      const rows = await getCustomerMovementHistory(customer.id, CUSTOMER_HISTORY_PAGE_SIZE, 0, {});
      setCustomerHistoryRows(rows);
      setCustomerHistoryOffset(rows.length);
      setCustomerHistoryHasMore(rows.length === CUSTOMER_HISTORY_PAGE_SIZE);
    } finally {
      setCustomerHistoryLoading(false);
    }
  }, []);

  const closeCustomerHistory = useCallback(() => {
    setCustomerHistoryVisible(false);
    setCustomerHistoryCustomer(null);
    setCustomerHistoryRows([]);
    setCustomerHistoryOffset(0);
    setCustomerHistoryHasMore(false);
    setCustomerHistoryTypeFilter('all');
    setCustomerHistoryFromDate('');
    setCustomerHistoryToDate('');
  }, []);

  const applyCustomerHistoryFilters = useCallback(async () => {
    if (!customerHistoryCustomer) return;
    // Validar que from <= to
    const from = customerHistoryFromDate.trim();
    const to = customerHistoryToDate.trim();
    if (from && to) {
      const fromTs = new Date(`${from}T00:00:00`).getTime();
      const toTs = new Date(`${to}T00:00:00`).getTime();
      if (Number.isFinite(fromTs) && Number.isFinite(toTs) && fromTs > toTs) {
        showError('Filtros', 'La fecha "desde" no puede ser mayor que la fecha "hasta".');
        return;
      }
    }
    setCustomerHistoryLoading(true);
    setCustomerHistoryOffset(0);
    setCustomerHistoryHasMore(false);
    setCustomerHistoryLoadingMore(false);
    try {
      const rows = await getCustomerMovementHistory(
        customerHistoryCustomer.id,
        CUSTOMER_HISTORY_PAGE_SIZE,
        0,
        buildCustomerHistoryFilters(),
      );
      setCustomerHistoryRows(rows);
      setCustomerHistoryOffset(rows.length);
      setCustomerHistoryHasMore(rows.length === CUSTOMER_HISTORY_PAGE_SIZE);
    } finally {
      setCustomerHistoryLoading(false);
    }
  }, [customerHistoryCustomer, buildCustomerHistoryFilters]);

  const loadMoreCustomerHistory = useCallback(async () => {
    if (!customerHistoryCustomer || customerHistoryLoadingMore || !customerHistoryHasMore) return;
    setCustomerHistoryLoadingMore(true);
    try {
      const rows = await getCustomerMovementHistory(
        customerHistoryCustomer.id,
        CUSTOMER_HISTORY_PAGE_SIZE,
        customerHistoryOffset,
        buildCustomerHistoryFilters(),
      );
      setCustomerHistoryRows((current) => [...current, ...rows]);
      setCustomerHistoryOffset((current) => current + rows.length);
      setCustomerHistoryHasMore(rows.length === CUSTOMER_HISTORY_PAGE_SIZE);
    } catch (error) {
      console.warn('[Customer] No se pudo cargar más historial', error);
      setCustomerHistoryHasMore(false);
    } finally {
      setCustomerHistoryLoadingMore(false);
    }
  }, [customerHistoryCustomer, customerHistoryHasMore, customerHistoryLoadingMore, customerHistoryOffset, buildCustomerHistoryFilters]);

  const exportCustomerHistoryCsv = useCallback(async () => {
    if (!customerHistoryCustomer) return;
    try {
      const rows = await getCustomerMovementHistoryForExport(customerHistoryCustomer.id, buildCustomerHistoryFilters());
      if (rows.length === 0) {
        showInfo('Exportar', 'No hay movimientos para exportar.');
        return;
      }
      const csv = buildCustomerMovementHistoryCsv({
        customerName: customerHistoryCustomer.name,
        rows,
        actorLabel,
        formatMoney,
      });
      const safeName = customerHistoryCustomer.name.replace(/[^\w\-]+/g, '_').slice(0, 40);
      await shareOrDownloadCsv(csv, `historial_fiado_${safeName}.csv`);
    } catch (e) {
      console.warn('[Customer] export CSV historial', e);
      showError('Exportar', 'No se pudo generar el archivo. Reintentá.');
    }
  }, [customerHistoryCustomer, buildCustomerHistoryFilters, actorLabel, formatMoney]);

  return {
    customerHistoryVisible,
    customerHistoryCustomer,
    customerHistoryRows,
    customerHistoryLoading,
    customerHistoryLoadingMore,
    customerHistoryHasMore,
    customerHistoryTypeFilter,
    setCustomerHistoryTypeFilter,
    customerHistoryFromDate,
    customerHistoryToDate,
    setCustomerHistoryFromDate,
    setCustomerHistoryToDate,
    customerTypeOptions,
    actorLabel,
    openCustomerHistory,
    closeCustomerHistory,
    applyCustomerHistoryFilters,
    loadMoreCustomerHistory,
    exportCustomerHistoryCsv,
  };
}
